import { portalText } from "../i18n/portal.js";
/**
 * "Can the signed-in member do X?" — one place the console asks, so no screen restates the matrix.
 *
 * Customer-portal review 2026-09-25 #134 / #135: a Member saw the invite form and every role box, a
 * Viewer saw "New agent" and pages that only answered "could not be read… This route is open to
 * owner, admin, member" — controls and pages offered to roles the server then refused. The fix is
 * to OFFER only what the role can use, and to say why when something is withheld.
 *
 * **THIS DECIDES WHAT IS OFFERED, NEVER WHAT IS ALLOWED.** The API refuses by role
 * (`apps/api/src/http/authenticate.ts`) and the database refuses again (RLS, and 0031 for Owner
 * rows). Hiding a control here is courtesy; forgetting to hide one is a confusing 403, not a hole.
 *
 * **TWO KINDS OF ACT, AND BOTH COME FROM SOMEWHERE ELSE.**
 *
 *   · `capability` — a WORKSPACE capability, asked of `packages/domain`'s canonical evaluator
 *     (`capability.ts`, `holdsWorkspaceCapability`) with the member's role in the workspace.
 *     Nothing here re-encodes a tick.
 *   · `accountCapability` — an ORGANIZATION capability (plan-014 A3, design §27.5.2), asked of the
 *     same module with the person's ACCOUNT role. Creating and renaming a workspace and spending the
 *     organization's money are account capabilities now; a workspace role never grants one.
 *   · `routeRole` — a page whose reading route declares a role list the matrix has no capability
 *     for (contact lists, do-not-call, compliance, the audit log, test calls). The list is the
 *     route's own `memberRole`, copied with the route named beside it, and `member-capability.test.ts`
 *     pins each one so a drift is a failing test rather than a silently wrong sidebar.
 *
 * New screens adopt it with `useMemberCan()` (a hook over the auth_session query) or, outside React,
 * `memberCan(role, act)` and `whyNot(role, act)` — or `consoleCan(roles, act)` / `consoleWhyNot` when
 * they hold both roles.
 *
 * AMENDED 2026-10-05 (plan-014 A3): derived from the canonical evaluator; the seven member roles; the
 * account acts. THE ACCOUNT ROLE IS NOT ON THE CONSOLE'S AUTH_SESSION VIEW YET — A4 adds it to the
 * gateway's auth context (plan-014 §3 A4). Until then {@link useMemberCan} knows no account role,
 * so an account act is not offered (the API refuses it to anyone without one, so offering it would
 * be the confusing 403 this module exists to avoid) and its refusal names the organization's roles.
 *
 * AMENDED 2026-10-05 (plan-014 A4): the account role has arrived — {@link useMemberCan} reads it
 * from the auth_session's context (`accountRole`, in a workspace and with the organization alone
 * open), and creating and renaming a workspace are `accountCapability` rows now, as the routes judge
 * them (`account.workspace.create`, `account.workspace.manage`).
 *
 * AMENDED 2026-10-09 (ADR 0041): a `capability` row is ALSO held by the account roles that hold that workspace
 * capability on every workspace of the organization (`accountRoleHoldsWorkspaceCapability` — today `membership.read`
 * and `workspace.lifecycle`, the organization's Owner and Admin), so "Delete workspace" and "Archive" are offered to
 * them on any workspace, and the refusal names them beside the workspace's own roles.
 *
 * AMENDED 2026-10-10 (ADR 0052): `membership.role_change` and `membership.remove` joined the bridge, so the People
 * panel offers the organization's Owner and Admin the role boxes, Remove and Revoke on every workspace;
 * {@link peopleChangeRefusal} asks the capability module's `workspaceMembershipChangeOf` per row, as the route does.
 */
import {
  ACCOUNT_ROLE,
  MEMBER_ROLE,
  accountRoleHoldsWorkspaceCapability,
  accountRolesHolding,
  accountRolesHoldingWorkspaceCapability,
  assignableMemberRole,
  assignableWorkspaceRole,
  holdsAccountCapability,
  memberRemoveRefusal,
  memberRoleChangeRefusal,
  workspaceMembershipChangeOf,
  workspaceRolesHolding,
  type AccountCapability,
  type AccountRole,
  type MemberRole,
  type WorkspaceCapability,
} from "@wetalk/domain";
import { useQuery } from "@tanstack/react-query";

import { useGateway } from "../api/provider.js";
import { authContextQuery } from "../api/query.js";
import type { MemberRoleView, WorkspaceRoleView } from "../api/gateway.js";

type ActRule =
  | { readonly capability: WorkspaceCapability; readonly doing: string }
  | { readonly accountCapability: AccountCapability; readonly doing: string }
  | { readonly routeRole: readonly MemberRole[]; readonly route: string; readonly doing: string };

/**
 * Every act a console screen gates on, with the words its refusal uses ("Only Owners and Admins
 * can …"). Add a row here, never a role list in a screen.
 */
export const CONSOLE_ACT = {
  // --- the workspace and its people (Workspaces & team, Settings) ------------------------------
  // plan-014 A3 (design §27.5.3): the retired `workspace.create` / `workspace.delete` are the
  // organization's `account.workspace.create` / `account.workspace.manage` and the workspace's own
  // `workspace.lifecycle` (its Owner archives it from inside).
  //
  // plan-014 A4: creating and renaming are ORGANIZATION capabilities (`account.workspace.create`,
  // `account.workspace.manage`), judged by the account role the auth_session now carries — the rows
  // A3 left mirroring the routes' declared role list until it arrived.
  get "workspace.rename"(): ActRule { return {
    accountCapability: "account.workspace.manage",
    get doing() { return portalText("shell", "rename_a_workspace_or_change_its_time_zone_44100937"); },
  }; },
  get "workspace.create"(): ActRule { return { accountCapability: "account.workspace.create", get doing() { return portalText("shell", "create_a_workspace_723e5089"); } }; },
  get "workspace.delete"(): ActRule { return { capability: "workspace.lifecycle", get doing() { return portalText("shell", "delete_a_workspace_a3bd76fa"); } }; },
  get "membership.invite"(): ActRule { return { capability: "membership.invite", get doing() { return portalText("shell", "invite_people_665fb59e"); } }; },
  get "membership.role_change"(): ActRule { return { capability: "membership.role_change", get doing() { return portalText("shell", "change_someone_s_role_85f3831e"); } }; },
  get "membership.remove"(): ActRule { return { capability: "membership.remove", get doing() { return portalText("shell", "remove_people_bcdf202a"); } }; },
  // --- keys, money, agents ----------------------------------------------------------------------
  get "api_key.manage"(): ActRule { return { capability: "api_key.manage", get doing() { return portalText("shell", "see_and_create_api_keys_4736add1"); } }; },
  // The retired `billing.write`: spending the organization's money is `account.finance.manage`.
  get "billing.write"(): ActRule { return { accountCapability: "account.finance.manage", get doing() { return portalText("shell", "add_credit_or_change_the_card_2566f463"); } }; },
  get "voice_agent.write"(): ActRule { return { capability: "voice_agent.write", get doing() { return portalText("shell", "create_or_change_an_agent_29410c89"); } }; },
  // --- pages whose reading route names its roles directly --------------------------------------
  get "contact_list.read"(): ActRule { return {
    // plan-014 review C-2: the route admits the presets holding `contact_list.manage` (Campaign manager).
    routeRole: ["owner", "admin", "member", "campaign_manager"],
    get route() { return "GET /v1/contact-lists"; },
    get doing() { return portalText("shell", "open_contact_lists_4c81f459"); },
  }; },
  // Fix round R, CL #121: adding people, replacing them and the download are Owner or Admin.
  get "contact_list.people"(): ActRule { return {
    routeRole: ["owner", "admin"],
    get route() { return "POST /v1/contact-lists/{contact_list_id}/contacts"; },
    get doing() { return portalText("shell", "add_people_to_a_list_replace_them_or_download_it_99800d31"); },
  }; },
  get "do_not_call_entry.read"(): ActRule { return {
    // plan-014 review C-2: plus the presets holding `do_not_call_entry.add` (Campaign manager).
    routeRole: ["owner", "admin", "member", "campaign_manager"],
    get route() { return "GET /v1/do-not-call-entries"; },
    get doing() { return portalText("shell", "open_the_do_not_call_list_83aef15f"); },
  }; },
  // WAPP-DASH (2026-10-28): the dashboard's Campaigns card reads an agent's campaigns — every Q10 role, viewers
  // included, and the presets holding `campaign.operate` (Campaign manager), as the route's CAMPAIGN_READ_ACCESS.
  get "campaign.read"(): ActRule { return {
    routeRole: ["owner", "admin", "member", "viewer", "campaign_manager"],
    get route() { return "GET /v1/agents/{voice_agent_id}/campaigns"; },
    get doing() { return portalText("shell", "see_campaigns"); },
  }; },
  // 2026-11-03 (owner: "members and viewers should also"): the survey results download is every role that reads the
  // results (apps/api/src/routes/survey/survey-answer.ts SURVEY_ANSWER_EXPORT = CONVERSATION_READ_ROLE), the file's
  // content following the role; one person's answers are the respondents read's roles (SURVEY_ANSWER_READ).
  get "survey_result.export"(): ActRule { return {
    routeRole: ["owner", "admin", "member", "viewer", "developer", "campaign_manager"],
    get route() { return "POST /v1/agents/{voice_agent_id}/survey-results/exports"; },
    get doing() { return portalText("shell", "survey_result.export_doing"); },
  }; },
  get "survey_respondent.read"(): ActRule { return {
    routeRole: ["owner", "admin", "member", "developer", "campaign_manager"],
    get route() { return "GET /v1/agents/{voice_agent_id}/survey-results/respondents"; },
    get doing() { return portalText("shell", "survey_respondent.read_doing"); },
  }; },
  get "caller_detail.read"(): ActRule { return { capability: "caller_detail.read", get doing() { return portalText("shell", "caller_detail.read_doing"); } }; },
  get "compliance.read"(): ActRule { return {
    routeRole: ["owner", "admin"],
    get route() { return "GET /v1/compliance"; },
    get doing() { return portalText("shell", "open_compliance_settings_adfec51b"); },
  }; },
  get "audit_entry.read"(): ActRule { return {
    routeRole: ["owner", "admin"],
    get route() { return "GET /v1/audit-entries"; },
    get doing() { return portalText("shell", "read_the_audit_log_f474a1fc"); },
  }; },
  get "test_call.place"(): ActRule { return {
    // plan-014 review C-2: plus the presets holding `voice_agent.write` (Developer).
    routeRole: ["owner", "admin", "member", "developer"],
    get route() { return "POST /v1/agents/{voice_agent_id}/test-calls"; },
    get doing() { return portalText("shell", "place_test_calls_0a4fbcbd"); },
  }; },
  // WAPP-ENDCALL (2026-10-28): "End call" on a live outbound conversation — the dial routes' own role list
  // (apps/api/src/routes/outbound-conversation/route.ts DIAL_MEMBER_ROLE: starting and ending share it). Ring now on the
  // agent's Outbound tab is offered by the same act (2026-10-29).
  get "conversation.dial"(): ActRule { return {
    routeRole: ["owner", "admin", "member"],
    get route() { return "POST /v1/outbound-conversations/{conversation_id}/end"; },
    get doing() { return portalText("shell", "end_call.doing"); },
  }; },
  // plan-004 WC-81: the Web chat page's writes — connect, on/off, voice, allowed sites, disconnect.
  get "web_chat_widget.manage"(): ActRule { return {
    // plan-014 review C-2: plus the presets holding `channel_binding.write` (Developer).
    routeRole: ["owner", "admin", "developer"],
    get route() { return "PATCH /v1/messaging/media/web_chat/widgets/{channel_binding_id}"; },
    get doing() { return portalText("shell", "change_the_website_chat_46d9e980"); },
  }; },
  // plan-005 OP-38 (OD-P5): the Payouts page — the statement is Owner, Admin and Viewer (read-only).
  get "payout.read"(): ActRule { return {
    routeRole: ["owner", "admin", "viewer"],
    get route() { return "GET /v1/payouts"; },
    get doing() { return portalText("shell", "see_payouts_e61dc679"); },
  }; },
  // plan-008 (design §22.13.1): every SIP trunk write — connect, edit, password, inbound sign-in,
  // numbers, binding, connection check, test calls, disable, delete — is Owner or Admin
  // (apps/api/src/routes/sip-trunk/route.ts SIP_TRUNK_WRITE); reading the trunk cards is every role.
  get "sip_trunk.manage"(): ActRule { return {
    // plan-014 review C-2: plus the presets holding `channel_binding.write` (Developer).
    routeRole: ["owner", "admin", "developer"],
    get route() { return "POST /v1/sip-trunks"; },
    get doing() { return portalText("shell", "connect_or_change_a_sip_trunk_2d4f6699"); },
  }; },
} as const satisfies Readonly<Record<string, ActRule>>;

export type ConsoleAct = keyof typeof CONSOLE_ACT;

/** The roles a person holds that the console judges with: the workspace's, and the organization's. */
export interface ConsoleRoles {
  readonly memberRole: MemberRoleView | MemberRole | null;
  readonly accountRole: AccountRole | null;
}

/** Whether `act` is an organization act (judged by the account role). */
export function isAccountAct(act: ConsoleAct): boolean {
  return "accountCapability" in CONSOLE_ACT[act];
}

/** The WORKSPACE roles that may do `act`, strongest first; empty for an organization act. */
export function rolesFor(act: ConsoleAct): readonly MemberRole[] {
  const rule: ActRule = CONSOLE_ACT[act];
  if ("capability" in rule) return workspaceRolesHolding(rule.capability);
  if ("accountCapability" in rule) return [];
  return MEMBER_ROLE.filter((role) => rule.routeRole.includes(role));
}

/** The ACCOUNT roles that may do an organization act, strongest first; empty for a workspace act. */
export function accountRolesFor(act: ConsoleAct): readonly AccountRole[] {
  const rule: ActRule = CONSOLE_ACT[act];
  return "accountCapability" in rule ? accountRolesHolding(rule.accountCapability) : [];
}

/**
 * The ACCOUNT roles that hold a workspace act's capability on EVERY workspace of the organization (ADR 0041), strongest
 * first; empty for an organization act, a route-role act, and a workspace capability off the bridge.
 */
export function accountRolesAcrossWorkspacesFor(act: ConsoleAct): readonly AccountRole[] {
  const rule: ActRule = CONSOLE_ACT[act];
  return "capability" in rule ? accountRolesHoldingWorkspaceCapability(rule.capability) : [];
}

/** Whether a person holding `roles` may do `act`. An unknown role is never a guess: nothing is offered. */
export function consoleCan(roles: ConsoleRoles, act: ConsoleAct): boolean {
  const rule: ActRule = CONSOLE_ACT[act];
  if ("accountCapability" in rule) {
    return roles.accountRole !== null && holdsAccountCapability(roles.accountRole, rule.accountCapability);
  }
  // ADR 0041: the organization's Owner and Admin hold the bridged workspace capabilities on every workspace.
  if ("capability" in rule && roles.accountRole !== null && accountRoleHoldsWorkspaceCapability(roles.accountRole, rule.capability)) {
    return true;
  }
  return roles.memberRole !== null && rolesFor(act).includes(roles.memberRole);
}

/** Whether a member holding `role` (and no known account role) may do `act`. */
export function memberCan(role: MemberRoleView | MemberRole, act: ConsoleAct): boolean {
  return consoleCan({ memberRole: role, accountRole: null }, act);
}

const ROLE_PLURAL: Readonly<Record<MemberRole, string>> = {
  get owner() { return portalText("shell", "owners_4eaec4bc"); },
  get admin() { return portalText("shell", "admins_ed6b5243"); },
  get member() { return portalText("shell", "members_1cb449c1"); },
  get viewer() { return portalText("shell", "viewers_0ae62965"); },
  get developer() { return portalText("shell", "developers_71348be5"); },
  get campaign_manager() { return portalText("shell", "campaign_managers_6c877194"); },
  get billing() { return portalText("shell", "billing_members_af3ab6e7"); },
};

const ACCOUNT_ROLE_PLURAL: Readonly<Record<AccountRole, string>> = {
  get owner() { return portalText("shell", "owners_4eaec4bc"); },
  get admin() { return portalText("shell", "admins_ed6b5243"); },
  get finance() { return portalText("shell", "finance_members_71bd3185"); },
  get viewer() { return portalText("shell", "viewers_0ae62965"); },
};

function sentenceOf(named: readonly string[]): string {
  if (named.length === 0) return portalText("shell", "wetalk_support_74a6daad");
  if (named.length === 1) return named[0] ?? "";
  return portalText("shell", "value_and_value_dcf74a8c", { v0: String(named.slice(0, -1).join(", ")), v1: String(named.at(-1) ?? "") });
}

/** "Owners", "Owners and Admins", "Owners, Admins and Members" — the API's wording, too. */
export function rolesInPlainWords(role: readonly MemberRole[]): string {
  return sentenceOf(role.map((one) => ROLE_PLURAL[one]));
}

/** The same, for the organization's roles: "the organization's Owners and Admins". */
export function accountRolesInPlainWords(role: readonly AccountRole[]): string {
  return portalText("shell", "the_organization_s_value_38c81547", { v0: String(sentenceOf(role.map((one) => ACCOUNT_ROLE_PLURAL[one]))) });
}

/**
 * Who may do `act`, in plain words — workspace roles, or the organization's roles; for a bridged workspace act both
 * (ADR 0041): "Owners of the workspace and the organization's Owners and Admins".
 */
function holdersInPlainWords(act: ConsoleAct): string {
  if (isAccountAct(act)) return accountRolesInPlainWords(accountRolesFor(act));
  const across = accountRolesAcrossWorkspacesFor(act);
  const inWorkspace = rolesInPlainWords(rolesFor(act));
  return across.length === 0 ? inWorkspace : portalText("shell", "value_of_the_workspace_and_value_f8d47d93", { v0: String(inWorkspace), v1: String(accountRolesInPlainWords(across)) });
}

/** Why a person holding `roles` may not do `act`, in plain words — or `null` when they may. */
export function consoleWhyNot(roles: ConsoleRoles, act: ConsoleAct): string | null {
  if (consoleCan(roles, act)) return null;
  return portalText("shell", "only_value_can_value_8acb4275", { v0: String(holdersInPlainWords(act)), v1: String(CONSOLE_ACT[act].doing) });
}

/** Why `role` may not do `act`, in plain words — or `null` when it may. */
export function whyNot(role: MemberRoleView | MemberRole, act: ConsoleAct): string | null {
  return consoleWhyNot({ memberRole: role, accountRole: null }, act);
}

const ROLE_SINGULAR: Readonly<Record<MemberRole, string>> = {
  get owner() { return portalText("shell", "owner_89ff3122"); },
  get admin() { return portalText("shell", "admin_4e7afebc"); },
  get member() { return portalText("shell", "member_6853c98a"); },
  get viewer() { return portalText("shell", "viewer_dbad9f12"); },
  get developer() { return portalText("shell", "developer_7e08ebec"); },
  get campaign_manager() { return portalText("shell", "campaign_manager_ce843fd2"); },
  get billing() { return portalText("shell", "billing_abaec452"); },
};

/**
 * What a whole page says, in place of its content, to a role its reading route refuses — who
 * manages it and the reader's own role — or `null` when the role may read it.
 *
 * Customer-portal review 2026-09-25 #210: a Member opening Compliance read the API's raw "This
 * route is open to owner, admin." and then "Nothing on this screen is guessed while it cannot be
 * read…", which reads as an outage, not a permission. The page now says, e.g., "Compliance is
 * managed by the Owners and Admins of Roma Pizzeria. Your role here is Member." — and does not ask
 * the API at all.
 */
export function managedByText(
  page: string,
  act: ConsoleAct,
  role: MemberRoleView | MemberRole,
  accountName: string,
): string | null {
  if (memberCan(role, act)) return null;
  return portalText("shell", "value_is_managed_by_the_value_of_value_your_role_here_is_v_1a5174cb", { v0: String(page), v1: String(holdersInPlainWords(act).replace(/^the organization's /, "")), v2: String(accountName), v3: String(ROLE_SINGULAR[role]) });
}

/**
 * The roles `caller` may give someone: an Owner may make an Owner, nobody else may (review #132).
 * Empty when the caller may not change roles at all. AMENDED 2026-10-06 (plan-014 B3): the people
 * routes accept all seven workspace roles now, so all seven are offered (FR12-TEAM-05).
 */
export function assignableRole(caller: MemberRoleView | MemberRole): readonly MemberRoleView[] {
  return assignableMemberRole(caller);
}

/**
 * Whether `caller` may change or remove a member who holds `target`, and why not when they may
 * not. Only an Owner touches an Owner (review #132); the server and the database say the same —
 * all three from the capability module's admin limits.
 */
export function memberManageable(
  caller: MemberRoleView | MemberRole,
  target: MemberRoleView | MemberRole,
  act: "membership.role_change" | "membership.remove",
): string | null {
  const refused = whyNot(caller, act);
  if (refused !== null) return refused;
  const limit = act === "membership.remove" ? memberRemoveRefusal(caller, target) : memberRoleChangeRefusal(caller, target, target);
  if (limit === "owner_only") {
    return act === "membership.remove"
      ? portalText("shell", "only_an_owner_can_remove_an_owner_c4f8d79f")
      : portalText("shell", "only_an_owner_can_change_an_owner_s_role_e021ee6d");
  }
  return null;
}

/** ADR 0052: the roles a person holding `roles` may give someone in a workspace — the union of both authorities. */
export function assignableRoleFor(roles: ConsoleRoles): readonly MemberRoleView[] {
  return assignableWorkspaceRole(roles.memberRole, roles.accountRole);
}

/**
 * ADR 0052: whether a person holding `roles` (the role in THIS workspace, and the organization's) may change or remove a
 * member holding `target`, and why not when they may not — asked of the capability module's
 * `workspaceMembershipChangeOf`, as the route asks it. `lastOwner`: the target is the workspace's only Owner, refused in
 * `lastOwnerText` unless the organization's authority allows it. The console does not know whether the target is the
 * organization's Owner; the API refuses the organization's Admin that one, by name.
 */
export function peopleChangeRefusal(
  roles: ConsoleRoles,
  input: {
    readonly target: MemberRoleView | MemberRole;
    readonly act: "membership.role_change" | "membership.remove";
    readonly self: boolean;
    readonly lastOwner: boolean;
    readonly lastOwnerText: string;
  },
): string | null {
  const refused = consoleWhyNot(roles, input.act);
  if (refused !== null) return refused;
  const decision = workspaceMembershipChangeOf({
    act: input.act,
    memberRole: roles.memberRole,
    accountRole: roles.accountRole,
    targetRole: input.target,
    toRole: input.target,
    self: input.self,
    targetIsOrganizationOwner: false,
    lastOwnerAtStake: input.lastOwner,
  });
  if (decision.allowed) return null;
  if (decision.code === "last_owner") return input.lastOwnerText;
  if (decision.code === "capability_missing") return consoleWhyNot({ memberRole: null, accountRole: null }, input.act);
  if (input.act === "membership.role_change" && input.self) return portalText("shell", "you_cannot_change_your_own_role_here_another_owner_or_admin_can_359936b4");
  return input.act === "membership.remove"
    ? portalText("shell", "only_an_owner_can_remove_an_owner_c4f8d79f")
    : portalText("shell", "only_an_owner_can_change_an_owner_s_role_e021ee6d");
}

/** Every account role, strongest first — re-exported for screens that list the organization's roles. */
export const CONSOLE_ACCOUNT_ROLE: readonly AccountRole[] = ACCOUNT_ROLE;

export interface MemberCapability {
  /**
   * The signed-in member's role in the open workspace, or `null` while the auth_session has not
   * arrived — and with the organization alone open, where there is no workspace role to judge by.
   */
  readonly role: WorkspaceRoleView | null;
  /**
   * The person's role in the organization (plan-014 A4: from the auth_session's context), or `null`
   * while unknown and for workspace-only access.
   */
  readonly accountRole: AccountRole | null;
  /** False while the role is unknown: nothing is offered on a guess. */
  readonly can: (act: ConsoleAct) => boolean;
  /** The plain-words reason, or `null` when allowed (and while unknown, nothing to explain yet). */
  readonly whyNot: (act: ConsoleAct) => string | null;
}

/** The hook screens use. Reads the same auth_session query as the shell, so it costs no request. */
export function useMemberCan(): MemberCapability {
  const gateway = useGateway();
  const authSession = useQuery(authContextQuery(gateway)).data;
  const role = authSession?.kind === "workspace_selected" ? authSession.memberRole : null;
  const accountRole: AccountRole | null =
    authSession === undefined || authSession === null || authSession.kind === "selection_required"
      ? null
      : authSession.accountRole;
  const roles: ConsoleRoles = { memberRole: role, accountRole };
  // Nothing to explain while the auth_session has not arrived (or names no role at all).
  const known = role !== null || accountRole !== null;
  return {
    role,
    accountRole,
    can: (act) => consoleCan(roles, act),
    whyNot: (act) => (known ? consoleWhyNot(roles, act) : null),
  };
}
